import { loader } from "../scripts/loader";
import { setShadowBehaviour } from "../scripts/scene";
import { createLinkWithPath } from "./createLinkWithPath";
import { addLabelText, TEXT_MAP } from "./createTextSprite";
import { wrapMeshInGroup } from "./wrapMeshInGroup";
const targetTableNames = [
    "osnovanie",
    "stoleshnitsa",
    "stoyka",
    "stol__",
    "telezhka__",
    "tumba__",
    "stellazh__",
    "stul__",
    "shkaf__",
];

// Условия для элементов, которым необходимо включить флаг isBottom = true
const bottomConditions = [
    (el) => el.name.includes("osnovanie"),
    (el) => el.name.includes("stoyka__") && el.name.includes("mesh"),
    (el) => el.name.includes("stol__") && el.name.includes("mesh"),
    (el) => el.name.includes("telezhka__") && el.name.includes("mesh"),
    (el) => el.name.includes("tumba__") && el.name.includes("mesh"),
    (el) => el.name.includes("stellazh__") && el.name.includes("mesh"),
    (el) => el.name.includes("shkaf__") && el.isMesh,
    (el) => el.name.includes("stul__") && el.name.includes("pivot__top"),
];
export const loadGLB = (loadData) => {
    const { glbName, allArrTable, calcActives, defaultSize, glbPath } = loadData;
    const path = `./3d/${glbPath ?? ""}${glbName}`;

    return new Promise((resolve) => {
        loader.loadGLTF(path, async (gltf) => {
            const glb = gltf.scene;
            glb.name = glbName;

            const link = createLinkWithPath("textures");

            glb.traverse((el) => {
                if (el.material) el.material = el.material.clone();
                el.visible = false;

                if (bottomConditions.some((cond) => cond(el))) {
                    el.userData.isBottom = true;
                }
            });

            await applyAOTexturesToGLB(glb, link);
            servObj.scene.add(glb);

            const children = [...glb.children];

            for (const tableRaw of children) {
                // если это Mesh — оборачиваем в Group
                let table = ensureGroup(tableRaw, glb);

                if (!targetTableNames.some((name) => table.name.includes(name))) {
                    continue;
                }

                table.userData.defaultSize = defaultSize;
                table.userData.calcActives = calcActives;
                table.userData.loadData = loadData;

                const parts = table.name.split("__");
                const isWardrobe = parts[0] === "shkaf";
                const wardrobeType = parts[1];

                if (table.userData.hasLabel) continue;

                if (isWardrobe && TEXT_MAP[wardrobeType]) {
                    table.userData.hasLabel = true;

                    await addLabelText(table, wardrobeType);
                }

                table.children.forEach((mesh) => {
                    const map = mesh.material?.map;
                    const normalMap = mesh.material?.normalMap;

                    if (map) map.anisotropy = 16;
                    if (normalMap) normalMap.anisotropy = 16;
                });

                servObj[allArrTable] ||= [];
                servObj[allArrTable].push(table);
            }

            setShadowBehaviour(glb, [], []);
            resolve(glb);
        });
    });
};

function ensureGroup(object, parent) {
    if (object.isGroup) return object;

    if (object.isMesh) {
        const wrapped = wrapMeshInGroup(object);
        parent.remove(object);
        parent.add(wrapped);
        return wrapped;
    }

    return object;
}

/**
 * Загружает все AO-текстуры единовременно через loader.loadTextures.
 * Результаты сохраняются в servObj.aoTextureCache.map по имени файла.
 *
 * @param {string} basePath - путь до директории с текстурами, например '/textures'.
 * @returns {Promise<void>} разрешается после загрузки всех текстур.
 */
export function loadAllAOTextures(basePath, namesToLoad) {
    return new Promise((resolve, reject) => {
        if (namesToLoad.length === 0) {
            resolve();
            return;
        }
        loader.loadTextures(
            basePath,
            namesToLoad,
            (textures) => {
                namesToLoad.forEach((name) => {
                    servObj.aoTextureCache.map[name] = textures.find((_tex) => _tex.name === name);
                });
                resolve();
            },
            undefined,
            (err) => {
                console.error("Ошибка загрузки AO-текстур:", err);
                reject(err);
            },
        );
    });
}
/**
 * Применяет AO-текстуры ко всем объектам GLB-сцены.
 * Загружает каждый AO-файл один раз, затем переиспользует текстуру из кэша.
 *
 * @async
 * @param {THREE.Object3D} glb - корневой Object3D из GLTFLoader.
 * @param {string} basePath - путь к папке с AO-текстурами (дефолт '/textures').
 */

export async function applyAOTexturesToGLB(glb, basePath = "textures") {
    // 1) Собираем имена AO-файлов, которых ещё нет в кэше
    const namesToLoad = new Set();
    glb.traverse((obj) => {
        const ao = obj.userData?.ao;
        if (ao) {
            servObj.aoTextureCache.names.add(ao);
            if (!servObj.aoTextureCache.map[ao]) {
                namesToLoad.add(ao);
            }
        }
    });
    // 2) Загружаем только новые AO-текстуры
    if (namesToLoad.size > 0) {
        await loadAllAOTextures(basePath, Array.from(namesToLoad));
    }
    // 3) Рекурсивно применяем AO-карты с учётом наследования от родителя
    function applyAORecursive(obj, parentAOName = null) {
        const aoName = obj.userData?.ao || parentAOName;
        const texture = aoName ? servObj.aoTextureCache.map[aoName] : null;

        // Сюда строки name тех объектов, на которые не нужна AOmap
        const excludedNames = ["dopolnitelnoe_pokritie"];
        const isExcluded = excludedNames.some((name) => obj.name.includes(name));
        if (texture && obj.material && !isExcluded) {
            if (Array.isArray(obj.material)) {
                obj.material.forEach((mat) => {
                    mat.aoMap = texture;
                    mat.needsUpdate = true;
                });
            } else {
                obj.material.aoMap = texture;
                obj.material.needsUpdate = true;
            }
        }
        obj.children.forEach((child) => applyAORecursive(child, aoName));
    }
    applyAORecursive(glb, null);
    // setTimeout(() => {
    //     checkAOConsistency(glb);
    // }, 1500);
}
export function checkAOConsistency(glb) {
    glb.traverse((obj) => {
        if (obj.userData?.ao) {
            const aoName = obj.userData.ao;
            obj.traverse((child) => {
                if (child.material) {
                    const checkMaterial = (mat) => {
                        if (mat.aoMap && mat.aoMap.name !== aoName && !child.name.includes("hitbox")) {
                            console.warn("AO mismatch:", {
                                object: child.name,
                                expected: aoName,
                                actual: mat.aoMap.name,
                                material: mat,
                                parentWithAO: obj.name,
                            });
                        }
                    };
                    if (Array.isArray(child.material)) {
                        child.material.forEach(checkMaterial);
                    } else {
                        checkMaterial(child.material);
                    }
                }
            });
        }
    });
}
